import { Link } from "react-router-dom";
import Swal from "sweetalert2";
import { useGetProducts, useUpdateProduct, useDeleteProduct } from "../backend/hooks";
import { toast } from "react-toastify";
import React from "react";
import ReactDOM from "react-dom/client";
import ReactQuill from "react-quill-new";
import { normalizeRichText } from "../utils/richText";
import { getVariants, getPriceRange } from "../utils/variants";
import "react-quill-new/dist/quill.snow.css";
import { PageHeaderSkeleton, StatCardsSkeleton, TableSkeleton } from '../common/skeleton';
import ImageManager, { fromExisting, toUploadPayload } from "./imageManager";

// Helper component to embed Quill editor inside Swal modal
const QuillEditorWrapper = ({ initialValue, onReady }) => {
  const [value, setValue] = React.useState(initialValue || "");

  React.useEffect(() => {
    if (onReady) {
      onReady(() => value);
    }
  }, [value, onReady]);

  return (
    <ReactQuill
      theme="snow"
      value={value}
      onChange={setValue}
      placeholder="Enter technical description..."
      modules={{
        toolbar: [
          ["bold", "italic", "underline", "strike"],
          ["blockquote", "code-block"],
          [{ list: "ordered" }, { list: "bullet" }],
          ["link", "clean"],
        ],
      }}
      style={{ height: "200px", marginBottom: "40px" }}
    />
  );
};

// Holds the image list for the Swal edit modal, which lives outside React state
const ImageManagerWrapper = ({ initialImages, onReady }) => {
  const [items, setItems] = React.useState(() => fromExisting(initialImages));

  React.useEffect(() => {
    if (onReady) onReady(() => items);
  }, [items, onReady]);

  return <ImageManager value={items} onChange={setItems} />;
};

export default function InventoryProduct() {
  const { data, isLoading, error, refetch } = useGetProducts();
  const { mutate: updateProduct } = useUpdateProduct();
  const { mutate: deleteProduct } = useDeleteProduct();
  const getTagsArray = (tags) => {
    if (!tags) return [];
    if (Array.isArray(tags)) {
      return tags.flatMap((item) => item.split(",").map((s) => s.trim()));
    }
    return tags.split(",").map((s) => s.trim());
  };

  // One editable row per product option ("Standard Quality", "Large", ...).
  // The modal is plain HTML inside SweetAlert, so these are built as strings
  // and read back out of the DOM in preConfirm.
  const buildVariantRowHtml = (variant = { name: "", price: "", price_usd: "", in_stock: "yes" }) => {
    const name = String(variant.name || "").replace(/"/g, "&quot;");
    const soldOut = variant.in_stock === "no";
    const usd = variant.price_usd === "" || variant.price_usd === undefined || variant.price_usd === null ? "" : Number(variant.price_usd);
    return `
      <div class="variant-row flex flex-wrap items-center gap-2 mb-2">
        <input class="variant-name swal2-input !mt-0 !mb-0 flex-1 !min-w-[180px]" placeholder="Option name, e.g. Premium Quality" value="${name}" />
        <input class="variant-price swal2-input !mt-0 !mb-0 !w-28" type="number" step="0.01" placeholder="PKR" value="${variant.price === "" || variant.price === undefined ? "" : Number(variant.price)}" />
        <input class="variant-price-usd swal2-input !mt-0 !mb-0 !w-28" type="number" step="0.01" placeholder="USD" value="${usd}" />
        <select class="variant-stock swal2-select !mt-0 !mb-0 !min-w-0 !w-32">
          <option value="yes" ${soldOut ? "" : "selected"}>In stock</option>
          <option value="no" ${soldOut ? "selected" : ""}>Sold out</option>
        </select>
        <button type="button" class="variant-remove text-red-600 font-bold px-2 py-1 rounded hover:bg-red-50" aria-label="Remove option">&times;</button>
      </div>`;
  };

  const readVariantsFromModal = () =>
    Array.from(document.querySelectorAll("#edit-variants .variant-row"))
      .map((row) => ({
        name: (row.querySelector(".variant-name")?.value || "").trim(),
        price: row.querySelector(".variant-price")?.value,
        price_usd: row.querySelector(".variant-price-usd")?.value,
        in_stock: row.querySelector(".variant-stock")?.value === "no" ? "no" : "yes",
      }))
      .filter((variant) => variant.name !== "" || (variant.price !== "" && variant.price !== undefined));

  const handleEdit = (product) => {
    const tagString = getTagsArray(product.tags).join(", ");
    const existingVariants = Array.isArray(product.variants) ? product.variants : [];
    const variantsHtml = existingVariants.map((variant) => buildVariantRowHtml(variant)).join("");

    let getQuillContent = null;
    let quillRoot = null;
    let getImages = null;
    let imagesRoot = null;

    Swal.fire({
      title: `Edit Product: ${product.title}`,
      width: "950px",
      customClass: {
        popup: "rounded-xl",
        htmlContainer: "overflow-visible",
      },
      html: `
        <div class="text-left p-5 max-h-[70vh] overflow-y-auto">
          <div class="space-y-4">
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Product Name *</label>
              <input id="edit-title" class="swal2-input w-full" value="${product.title.replace(
                /"/g,
                "&quot;"
              )}" />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Category ID *</label>
              <input id="edit-category_id" class="swal2-input w-full" value="${product.category_id}" />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Description</label>
              <textarea id="edit-description" class="swal2-textarea w-full" rows="3">${(
                product.description || ""
              ).replace(/"/g, "&quot;")}</textarea>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Technical Description</label>
              <div id="quill-editor-container"></div>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Price *</label>
              <div id="edit-price-block" class="${existingVariants.length ? "hidden" : ""} flex gap-2">
                <input id="edit-price" type="number" step="0.01" class="swal2-input !mt-0 flex-1" placeholder="PKR" value="${product.price}" />
                <input id="edit-price-usd" type="number" step="0.01" class="swal2-input !mt-0 flex-1" placeholder="USD (optional)" value="${product.price_usd === null || product.price_usd === undefined ? "" : Number(product.price_usd)}" />
              </div>
              <div id="edit-price-options-note" class="${existingVariants.length ? "" : "hidden"} text-sm text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                Set per option below &ndash; customers pay the price of the option they pick,
                and the first option is shown by default.
              </div>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">In Stock</label>
              <select id="edit-in_stock" class="swal2-select w-full">
                <option value="yes" ${product.in_stock === "yes" ? "selected" : ""}>Yes</option>
                <option value="no" ${product.in_stock === "no" ? "selected" : ""}>No</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Delivery Charge</label>
              <p class="text-xs text-gray-500 mb-1">Charged once per order for this product. Leave blank for free delivery.</p>
              <div class="flex gap-2">
                <input id="edit-shipping-pkr" type="number" step="0.01" class="swal2-input !mt-0 flex-1" placeholder="Rs" value="${product.shipping_pkr === null || product.shipping_pkr === undefined ? "" : Number(product.shipping_pkr)}" />
                <input id="edit-shipping-usd" type="number" step="0.01" class="swal2-input !mt-0 flex-1" placeholder="$" value="${product.shipping_usd === null || product.shipping_usd === undefined ? "" : Number(product.shipping_usd)}" />
              </div>
            </div>
            <div>
              <div class="flex items-center justify-between mb-1">
                <label class="block text-sm font-semibold text-gray-700">Options / Qualities</label>
                <button type="button" id="edit-add-variant" class="text-sm font-bold text-primary hover:opacity-70">+ Add option</button>
              </div>
              <p class="text-xs text-gray-500 mb-2">
                Leave empty to sell at the single price above. Each option carries its own price.
              </p>
              <div id="edit-variants">${variantsHtml}</div>
              <p id="edit-variants-empty" class="text-xs text-gray-400 italic ${existingVariants.length ? "hidden" : ""}">
                No options &ndash; single price product.
              </p>
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Tags (comma separated)</label>
              <input id="edit-tags" class="swal2-input w-full" value="${tagString}" />
            </div>
            <div>
              <label class="block text-sm font-semibold text-gray-700 mb-1">Product Images</label>
              <div id="edit-images-container"></div>
            </div>
          </div>
        </div>
      `,
      showCancelButton: true,
      confirmButtonText: "Update Product",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0F67FE",
      didOpen: () => {
        const imagesContainer = document.getElementById("edit-images-container");
        if (imagesContainer) {
          imagesRoot = ReactDOM.createRoot(imagesContainer);
          imagesRoot.render(
            <ImageManagerWrapper
              initialImages={product.images}
              onReady={(getFn) => {
                getImages = getFn;
              }}
            />
          );
        }

        const variantsContainer = document.getElementById("edit-variants");
        const addVariantBtn = document.getElementById("edit-add-variant");
        const emptyHint = document.getElementById("edit-variants-empty");
        const priceBlock = document.getElementById("edit-price-block");
        const priceNote = document.getElementById("edit-price-options-note");
        // The single price field only applies while the product has no options
        const syncEmptyHint = () => {
          if (!variantsContainer) return;
          const hasOptions = variantsContainer.children.length > 0;
          emptyHint?.classList.toggle("hidden", hasOptions);
          priceBlock?.classList.toggle("hidden", hasOptions);
          priceNote?.classList.toggle("hidden", !hasOptions);
        };
        if (addVariantBtn && variantsContainer) {
          addVariantBtn.addEventListener("click", () => {
            variantsContainer.insertAdjacentHTML("beforeend", buildVariantRowHtml());
            syncEmptyHint();
          });
          variantsContainer.addEventListener("click", (e) => {
            const removeBtn = e.target.closest(".variant-remove");
            if (!removeBtn) return;
            removeBtn.closest(".variant-row")?.remove();
            syncEmptyHint();
          });
        }

        const container = document.getElementById("quill-editor-container");
        if (container) {
          quillRoot = ReactDOM.createRoot(container);
          quillRoot.render(
            <QuillEditorWrapper
              initialValue={product.technical_description || ""}
              onReady={(getFn) => {
                getQuillContent = getFn;
              }}
            />
          );
        }
      },
      willClose: () => {
        if (quillRoot) {
          quillRoot.unmount();
          quillRoot = null;
        }
        if (imagesRoot) {
          imagesRoot.unmount();
          imagesRoot = null;
        }
      },
      preConfirm: () => {
        const title = document.getElementById("edit-title")?.value.trim();
        const category_id = document.getElementById("edit-category_id")?.value.trim();
        const description = document.getElementById("edit-description")?.value.trim();
        const technical_description = normalizeRichText(getQuillContent ? getQuillContent() : "");
        const in_stock = document.getElementById("edit-in_stock")?.value;
        const variants = readVariantsFromModal();
        // The price fields are hidden once options exist, so the product price
        // tracks the first option - the one the storefront preselects.
        const price = variants.length
          ? Number(variants[0].price)
          : parseFloat(document.getElementById("edit-price")?.value);
        const priceUsdInput = document.getElementById("edit-price-usd")?.value;
        const price_usd = variants.length
          ? (variants[0].price_usd === '' || variants[0].price_usd === undefined ? '' : variants[0].price_usd)
          : priceUsdInput;
        const shipping_pkr = document.getElementById("edit-shipping-pkr")?.value;
        const shipping_usd = document.getElementById("edit-shipping-usd")?.value;
        const tags = document.getElementById("edit-tags")?.value.trim();

        if (!title) {
          Swal.showValidationMessage("Product name is required");
          return false;
        }
        if (!category_id) {
          Swal.showValidationMessage("Category ID is required");
          return false;
        }
        const badVariant = variants.find(
          (variant) => variant.name === "" || !Number.isFinite(Number(variant.price)) || Number(variant.price) < 0
        );
        if (badVariant) {
          Swal.showValidationMessage("Every option needs a name and a price of 0 or more.");
          return false;
        }
        if (!variants.length && (isNaN(price) || price <= 0)) {
          Swal.showValidationMessage("Price must be a positive number");
          return false;
        }

        const formData = new FormData();
        formData.append("title", title);
        formData.append("category_id", category_id);
        formData.append("description", description);
        if (technical_description) formData.append("technical_description", technical_description);
        formData.append("price", price);
        formData.append("price_usd", price_usd ?? "");
        formData.append("shipping_pkr", shipping_pkr ?? "");
        formData.append("shipping_usd", shipping_usd ?? "");
        formData.append("in_stock", in_stock);
        formData.append("tags", tags);
        formData.append(
          "variants",
          JSON.stringify(
            variants.map((variant) => ({
              name: variant.name,
              price: Number(variant.price),
              price_usd: variant.price_usd === '' || variant.price_usd === undefined ? null : Number(variant.price_usd),
              in_stock: variant.in_stock,
            }))
          )
        );

        // The full list in display order; the first image is the banner.
        // Images left out of image_order are removed from the product.
        const { files, order } = toUploadPayload(getImages ? getImages() : fromExisting(product.images));
        files.forEach((file) => formData.append("images", file));
        formData.append("image_order", JSON.stringify(order));
        return formData;
      },
    }).then((result) => {
      if (result.isConfirmed && result.value) {
        const formData = result.value;
        updateProduct(
          { id: product.id, formData },
          {
            onSuccess: () => {
              Swal.fire("Updated!", "Product has been updated.", "success");
              refetch();
            },
            onError: (err) => {
              Swal.fire("Error", err.message || "Update failed", "error");
            },
          }
        );
      }
    });
  };

  const handleDelete = (product) => {
    Swal.fire({
      title: "Are you sure?",
      text: `Delete "${product.title}"? This action cannot be undone.`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Yes, delete it!",
    }).then((result) => {
      if (result.isConfirmed) {
        Swal.fire({
          title: "Deleting...",
          text: "Please wait",
          allowOutsideClick: false,
          didOpen: () => Swal.showLoading(),
        });
        deleteProduct(product.id, {
          onSuccess: () => {
            Swal.fire("Deleted!", "Product has been deleted.", "success");
            refetch();
          },
          onError: (err) => {
            Swal.fire("Error", err.message || "Delete failed", "error");
          },
        });
      }
    });
  };

  if (isLoading) {
    return (
      <main className="min-h-screen py-2 px-2 space-y-10">
        <PageHeaderSkeleton withAction />
        <StatCardsSkeleton count={1} className="grid grid-cols-1 md:grid-cols-3 gap-6" />
        <TableSkeleton columns={6} rows={8} withImage />
      </main>
    );
  }
  if (error) return <div className="p-8 text-center text-error">Error: {error.message}</div>;

  return (
    <main className="min-h-screen py-2 px-2">
      <header className="flex flex-col md:flex-row md:items-end justify-between mb-10 gap-4">
        <div>
          <span className="text-xs font-bold text-on-surface-variant uppercase tracking-[0.2em] mb-2 block">
            Tactile Inventory
          </span>
          <h2 className="text-4xl font-extrabold text-on-surface font-headline tracking-tight">
            Product Management
          </h2>
        </div>
        <Link
          to="/dashboard/add-product"
          className="inline-flex items-center px-6 py-3 bg-primary text-on-primary rounded-xl font-bold transition-transform active:scale-95 duration-200"
        >
          <span className="material-symbols-outlined mr-2">add_circle</span>
          Add New Product
        </Link>
      </header>

      <section className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
        <div className="bg-surface-container-lowest p-6 rounded-2xl transition-all hover:bg-white flex items-center gap-5">
          <div className="w-12 h-12 rounded-full bg-primary-fixed-dim/20 flex items-center justify-center">
            <span className="material-symbols-outlined text-primary" style={{ fontVariationSettings: "'FILL' 1" }}>
              inventory_2
            </span>
          </div>
          <div>
            <p className="text-sm font-medium text-on-surface-variant">Total Products</p>
            <p className="text-2xl font-bold text-on-surface">{data?.count || 0}</p>
          </div>
        </div>
      </section>

      <div className="bg-surface-container-low p-1 rounded-3xl">
        <div className="bg-surface-container-lowest rounded-[1.25rem] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-surface-container-low text-xs font-bold text-on-surface-variant uppercase tracking-widest">
                  <th className="px-6 py-4 min-w-[220px]">Product</th>
                  <th className="px-6 py-4 min-w-[120px]">Category</th>
                  <th className="px-6 py-4 min-w-[180px]">Tags</th>
                  <th className="px-6 py-4 min-w-[100px]">Stock</th>
                  <th className="px-6 py-4 min-w-[100px]">Price (PKR)</th>
                  <th className="px-6 py-4 text-right min-w-[100px]">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-surface-container-high">
                {data?.products?.map((prod) => (
                  <tr key={prod.id} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-6 py-4 min-w-[220px]">
                      <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-xl bg-slate-100 flex-shrink-0 overflow-hidden">
                          <img
                            className="w-full h-full object-cover"
                            alt={prod.title}
                            src={prod.images?.[0] ? process.env.REACT_APP_BE_URL + prod.images[0] : "https://via.placeholder.com/56"}
                          />
                        </div>
                        <div>
                          <p className="font-bold text-on-surface break-words">{prod.title}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-sm font-mono text-outline min-w-[120px]">{prod.category_id}</td>
                    <td className="px-6 py-4 min-w-[180px]">
                      <div className="flex flex-wrap gap-1">
                        {getTagsArray(prod.tags).map((tag, idx) => (
                          <span key={idx} className="inline-flex items-center px-3 py-1 bg-secondary-fixed text-on-secondary-fixed-variant text-xs font-bold rounded-full whitespace-nowrap">
                            {tag}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-6 py-4 min-w-[100px]">
                      {prod.in_stock === "yes" ? (
                        <span className="text-green-600 font-semibold whitespace-nowrap">In Stock</span>
                      ) : (
                        <span className="text-red-500 whitespace-nowrap">Out of Stock</span>
                      )}
                    </td>
                    <td className="px-6 py-4 font-bold text-on-surface min-w-[100px]">
                      {getVariants(prod).length > 0 ? (
                        <>
                          <span className="block">Rs {(getPriceRange(prod).min ?? 0).toFixed(2)}+</span>
                          <span className="block text-[10px] font-bold uppercase tracking-wider text-primary mt-0.5">
                            {getVariants(prod).length} options
                          </span>
                        </>
                      ) : (
                        <>Rs {prod.price}</>
                      )}
                    </td>
                    {/* Buttons sit in the row itself: a dropdown here was clipped by
                        the table's scroll container on the last rows. */}
                    <td className="px-6 py-4 min-w-[120px]">
                      <div className="flex justify-end gap-1">
                        <button onClick={() => handleEdit(prod)} className="p-2 rounded-lg text-on-surface-variant hover:text-primary hover:bg-primary/10 transition-colors" title="Edit product" aria-label={`Edit ${prod.title}`}>
                          <span className="material-symbols-outlined">edit</span>
                        </button>
                        <button onClick={() => handleDelete(prod)} className="p-2 rounded-lg text-error hover:bg-error/10 transition-colors" title="Delete product" aria-label={`Delete ${prod.title}`}>
                          <span className="material-symbols-outlined">delete</span>
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </main>
  );
}